import { Button, ButtonProps, Flex, Input, Text } from "@chakra-ui/react";
import { CustomModal } from "components/custom-modal";
import { InputField } from "components/input-field";
import {
  DraggableFileField,
  DraggableFileFieldProps,
} from "components/input-file";
import { UploadIcon } from "components/vectors/upload-icon";

import { useState } from "react";
import { useEventListener, makeEvent } from "services/events";

export type ModalUploadFileOptions = {
  onConfirm?: (file: File, description: string) => any;
  onOpen?: () => any;
  onClose?: () => any;
  onReject?: () => any;
  message?:
    | string
    | JSX.Element
    | (({ onClose }: { onClose: () => void }) => string | JSX.Element);
  title?: JSX.Element | string;
  confirmButtonStyle?: ButtonProps;
  rejectButtonStyle?: ButtonProps;
  variant?: "normal" | "danger";
  fileFieldProps?: Partial<DraggableFileFieldProps>;
  showDescriptionField?: boolean;
};

export function ModalUploadFile() {
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [modalConfirmOptions, setModalConfirmOptions] =
    useState<ModalUploadFileOptions | null>(null);

  function onOpen(data: ModalUploadFileOptions) {
    setModalConfirmOptions(data);
  }

  function onClose() {
    modalConfirmOptions?.onClose?.();
    setModalConfirmOptions(null);
    setFile(null);
  }

  const { onClick: rejectButtonOnClick, ...rejectButtonStyle } =
    modalConfirmOptions?.rejectButtonStyle || {};
  const { onClick: confirmButtonOnClick, ...confirmButtonStyle } =
    modalConfirmOptions?.confirmButtonStyle || {};

  useEventListener("openModalUploadFile", (data: ModalUploadFileOptions) => {
    if (modalConfirmOptions !== null) {
      new Error("The upload modal is already open");
    } else onOpen(data);
  });

  const modalFooter = modalConfirmOptions ? (
    <>
      <Button
        variant="outline"
        onClick={(e) => {
          modalConfirmOptions.onReject?.();
          onClose();
          rejectButtonOnClick?.(e);
        }}
        children="Cancelar"
        isDisabled={isLoading}
        {...rejectButtonStyle}
      />
      <Button
        onClick={async (e) => {
          setIsLoading(true);
          try {
            await modalConfirmOptions.onConfirm?.(file!, description!);
          } catch {}
          setIsLoading(false);
          onClose();
          confirmButtonOnClick?.(e);
        }}
        isLoading={isLoading}
        leftIcon={<UploadIcon width="18px" height="18px" />}
        children="Upload"
        isDisabled={!file}
        {...confirmButtonStyle}
      />
    </>
  ) : undefined;

  return (
    <>
      <CustomModal
        isOpen={modalConfirmOptions !== null}
        onClose={onClose}
        modalFooter={modalFooter}
        modalTitle={modalConfirmOptions?.title ?? "Upload de arquivo"}
        size="md"
      >
        {modalConfirmOptions?.message ? (
          <Flex alignItems="center" gap="10px" mb="10px" mt="10px">
            {typeof modalConfirmOptions?.message === "function"
              ? modalConfirmOptions.message({ onClose })
              : modalConfirmOptions?.message
              ? modalConfirmOptions.message
              : "Insira um arquivo:"}
          </Flex>
        ) : null}
        <DraggableFileField
          file={file}
          handleFile={async (file) => {
            setFile(file);
          }}
          handleDelete={() => {
            setFile(null);
          }}
          error=""
          description={
            <Text userSelect="none">
              Arraste um arquivo para cá ou <b>clique para inserir</b>
            </Text>
          }
          {...modalConfirmOptions?.fileFieldProps}
        />
      </CustomModal>
    </>
  );
}

export function openModalUploadFile(options: ModalUploadFileOptions) {
  makeEvent("openModalUploadFile", options);
}
